import React from "react";
import i18n from "i18next";
import styled from "styled-components";
import { connect, useStore } from "react-redux";
import { DialogOverlay, Dialog } from "components/dialog/DialogOverlay";
import {
  planningRulesSelector,
  searchTickersForText,
  currentPortfolioSelector,
  tickerTypes,
  getTickerDescription,
  convertCurrency,
  getTickerUsingId,
  planningVariables,
  getPrivateCustodians,
  PVST_VALUE_TICKER_ID,
  getTickersForIds,
  getExchangeRate
} from "@kubera/common";
import { addKeyboardEventListener, removeKeyboardEventListener } from "utilities/EventManager";
import CurrencyLabel from "components/labels/CurrencyLabel";
import SearchInput from "components/inputs/SearchInput";
import Loader from "components/loader/Loader";

const tickerCategory = {
  STOCKS_AND_FUNDS: "Stocks & funds",
  METALS: "Metals",
  CRYPTO: "Crypto",
  OTHER: "Other"
};

const PickerDialog = styled(Dialog)`
  position: relative;
  width: 612px;
  min-height: 633px;
  display: flex;
  align-items: stretch;
  margin-top: 74px;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  margin: 50px 50px 50px 50px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: left;
  flex: 1;
`;

const TickerCategoryContainer = styled.div`
  display: flex;
  font-style: normal;
  font-weight: 700;
  font-size: 22px;
  font-feature-settings: "ss01" on;
`;

const TickerCategory = styled.div`
  margin-right: 20px;
  color: ${props => (props.isSelected === true ? "#000000" : "rgba(0, 0, 0, 0.5)")};
  cursor: pointer;
`;

const SearchBox = styled(SearchInput)`
  height: 45px;
  margin-top: 14px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  font-feature-settings: "ss01" on;
  line-height: 17px;
  background-color: #ffffff;
`;

const ContentContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ResultsContainer = styled.div`
  position: absolute;
  top: 0;
  z-index: 1000;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  background: #ffffff;
`;

const Result = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 18px 12px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: ${props => (props.isFocused === true ? props.theme.focusBackgroundColor : "#ffffff")};
  cursor: pointer;
`;

const EmptyResult = styled(Result)`
  cursor: auto;
  font-size: 14px;
  padding-left: 10px;
`;

const ResultDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ResultName = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  font-feature-settings: "ss01" on, "calt" off;
  color: #000000;
`;

const ResultDescription = styled.div`
  margin-top: 3px;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  font-feature-settings: "ss01" on;
  color: rgba(0, 0, 0, 0.5);
`;

const ResultValue = styled(CurrencyLabel)`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  text-align: right;
  font-feature-settings: "ss01" on, "calt" off;
  color: #000000;
  visibility: ${props => (props.hideTickerPrice === true ? "hidden" : "visible")};
`;

const PVSTRow = styled.div`
  background-color: white;
  height: 59px;
  padding-left: 17px;
  font-size: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: ${props => (props.isFirst ? "14px" : "-1px")};
  cursor: pointer;
`;

const RateLoader = styled(Loader)`
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.popoverBackgroundColor};
  z-index: 1000;
`;

class TickersPickerDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchText: "",
      searchResults: null,
      searchLoading: false,
      currentlySelectedResultIndex: null,
      selectedItems: this.getDefaultItems(),
      selectedTickerCategory: tickerCategory.STOCKS_AND_FUNDS,
      isTickerRateLoading: false
    };

    this.handleSearchInputChange = this.handleSearchInputChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleResultInteraction = this.handleResultInteraction.bind(this);
    this.handleCategoryTitleClick = this.handleCategoryTitleClick.bind(this);
    this.handleSaveClick = this.handleSaveClick.bind(this);

    this.searchInputRef = React.createRef();
  }

  getDefaultItems() {
    if (!this.props.data === true || !this.props.data.items === true) {
      return [];
    }
    return this.props.data.items
      .map(obj => {
        if (obj?.id !== PVST_VALUE_TICKER_ID) return getTickerUsingId(this.props.store.getState(), obj.id);
        const myCust = this.props.privateCustodians.get(obj.pvstCustodianId);
        return myCust ? { ...obj, code: myCust.name, pvstRate: myCust.pvstRate } : null;
      })
      .filter(Boolean);
  }

  componentDidMount() {
    addKeyboardEventListener(this.handleKeyDown);
  }

  componentWillUnmount() {
    removeKeyboardEventListener(this.handleKeyDown, true);
  }

  handleKeyDown(e) {
    if (e.key === "ArrowUp") {
      this.selectPreviousResult();
      e.preventDefault();
      return true;
    } else if (e.key === "ArrowDown") {
      this.selectNextResult();
      e.preventDefault();
      return true;
    } else if (e.key === "Enter") {
      if (this.state.searchResults && this.state.currentlySelectedResultIndex !== null) {
        const result = this.state.searchResults[this.state.currentlySelectedResultIndex];
        this.handleResultInteraction(result);
      } else if (this.state.selectedItems?.length) {
        this.handleSaveClick();
      }
      e.preventDefault();
      return true;
    }
    return false;
  }

  allowUnusedTickers() {
    if (!this.props.data === false) {
      if (!this.props.data.props === false) {
        return this.props.data.props.allowUnusedTickers;
      } else if (!this.props.rule === false) {
        const ruleObject = this.props.planningRules.find(item => item.type === this.props.rule.type);
        if (
          !ruleObject.data === false &&
          !ruleObject.data[planningVariables.TICKER_ID_OBJ] === false &&
          !ruleObject.data[planningVariables.TICKER_ID_OBJ].props === false
        ) {
          return ruleObject.data[planningVariables.TICKER_ID_OBJ].props.allowUnusedTickers;
        }
      }
    }
    return undefined;
  }

  handleSearchInputChange(searchText) {
    this.setState({ searchText: searchText });

    if (!this.searchTimer === false) {
      clearTimeout(this.searchTimer);
    }

    if (!searchText === true) {
      this.setState({ searchText: searchText, searchResults: null, searchLoading: false });
      return;
    } else {
      this.setState({ searchText: searchText });
    }

    var tickerTypesAllowed = tickerTypes.FIAT;
    if (this.state.selectedTickerCategory === tickerCategory.STOCKS_AND_FUNDS) {
      tickerTypesAllowed = tickerTypes.STOCK;
    } else if (this.state.selectedTickerCategory === tickerCategory.CRYPTO) {
      tickerTypesAllowed = tickerTypes.CRYPTO;
    }

    this.setState({ searchLoading: true });

    this.searchTimer = setTimeout(() => {
      this.props.searchTickersForText(
        searchText,
        tickerTypesAllowed,
        this.allowUnusedTickers(),
        this.props.fetchTickersWithoutRate,
        data => {
          if (data.searchText !== searchText || this.state.searchText !== searchText) {
            return;
          }
          this.setState({ searchResults: data.results, searchLoading: false });
        }
      );
      this.searchTimer = null;
    }, 300);
  }

  handleResultInteraction(result) {
    if (result && !this.state.selectedItems.find(item => item.id === result.id) === true) {
      var selectedItems = this.props.allowMultiple === true ? this.state.selectedItems : [];
      selectedItems.push(result);
      this.setState({ selectedItems: selectedItems }, this.handleSaveClick);
    }

    this.handleSearchInputChange("");
  }

  handleCategoryTitleClick(e, category) {
    this.setState({ selectedTickerCategory: category });
    this.handleSearchInputChange("");
    if (category === tickerCategory.OTHER) return;

    setTimeout(() => {
      if (!this.searchInputRef.current === false) {
        this.searchInputRef.current.focus();
      }
    }, 10);
  }

  handleSaveClick(e) {
    const items = this.state.selectedItems.map(item => ({ id: item.id, pvstCustodianId: item.pvstCustodianId }));

    const onSuccess = () => {
      this.props.onVariableUpdate({ items: items });
      this.props.onDismiss();
    };

    if (this.props.fetchTickersWithoutRate === true) {
      var unknownTickerIds = new Set();
      for (const item of this.state.selectedItems) {
        if (
          getExchangeRate(
            this.props.store.getState(),
            getTickerUsingId(this.props.store.getState(), item.id).shortName,
            this.props.currentPortfolio.currency
          ) === 0
        ) {
          unknownTickerIds.add(item.id);
        }
      }

      if (unknownTickerIds.size === 0) {
        onSuccess();
      } else {
        this.setState({ isTickerRateLoading: true });
        this.props.getTickersForIds(
          Array.from(unknownTickerIds),
          () => {
            this.setState({ isTickerRateLoading: false });
            onSuccess();
          },
          apiError => {
            this.setState({ isTickerRateLoading: false });
          }
        );
      }
    } else {
      onSuccess();
    }
  }

  getResultDescription(result) {
    return getTickerDescription(result);
  }

  getResultValue(result) {
    if (result.id === PVST_VALUE_TICKER_ID && (result.pvstRate || result.pvstRate === 0)) return result.pvstRate;
    return convertCurrency(this.props.store.getState(), 1, result.shortName, result.currency);
  }

  selectNextResult() {
    const currentIndex = this.state.currentlySelectedResultIndex;

    if (this.state.searchResults && currentIndex === this.state.searchResults.length - 1) {
      return;
    }
    this.setState({ currentlySelectedResultIndex: currentIndex === null ? 0 : currentIndex + 1 });
  }

  selectPreviousResult() {
    const currentIndex = this.state.currentlySelectedResultIndex;
    if (currentIndex === null || currentIndex === 0) {
      return;
    }
    this.setState({ currentlySelectedResultIndex: currentIndex - 1 });
  }

  selectResult(index) {
    this.setState({ currentlySelectedResultIndex: index });
  }

  render() {
    const privateCustodiansArray =
      this.props.hidePrivateCustodians === true
        ? []
        : (this.props.privateCustodians && Array.from(this.props.privateCustodians.values())) || [];
    return (
      <DialogOverlay onDismiss={this.props.onDismiss}>
        <PickerDialog>
          <Container>
            {this.state.isTickerRateLoading && <RateLoader />}
            <TickerCategoryContainer>
              <TickerCategory
                isSelected={this.state.selectedTickerCategory === tickerCategory.STOCKS_AND_FUNDS}
                onClick={e => this.handleCategoryTitleClick(e, tickerCategory.STOCKS_AND_FUNDS)}
              >
                {i18n.t("stocks")}
              </TickerCategory>
              <TickerCategory
                isSelected={this.state.selectedTickerCategory === tickerCategory.METALS}
                onClick={e => this.handleCategoryTitleClick(e, tickerCategory.METALS)}
              >
                {i18n.t("metals")}
              </TickerCategory>
              <TickerCategory
                isSelected={this.state.selectedTickerCategory === tickerCategory.CRYPTO}
                onClick={e => this.handleCategoryTitleClick(e, tickerCategory.CRYPTO)}
              >
                {i18n.t("crypto")}
              </TickerCategory>
              {privateCustodiansArray?.length > 0 && (
                <TickerCategory
                  isSelected={this.state.selectedTickerCategory === tickerCategory.OTHER}
                  onClick={e => this.handleCategoryTitleClick(e, tickerCategory.OTHER)}
                >
                  {i18n.t("other")}
                </TickerCategory>
              )}
            </TickerCategoryContainer>
            {this.state.selectedTickerCategory !== tickerCategory.OTHER ? (
              <SearchBox
                outerRef={this.searchInputRef}
                autoFocus={true}
                clearOnEscape={true}
                isLoading={this.state.searchLoading}
                value={this.state.searchText}
                onChange={this.handleSearchInputChange}
                placeholder={this.state.selectedTickerCategory}
              />
            ) : (
              privateCustodiansArray.map((custodian, index) => (
                <PVSTRow
                  key={custodian.id}
                  isFirst={index === 0}
                  onClick={() => {
                    this.setState(
                      { selectedItems: [{ id: PVST_VALUE_TICKER_ID, pvstCustodianId: custodian.id }] },
                      () => {
                        this.handleSaveClick();
                      }
                    );
                  }}
                >
                  {custodian.name}
                </PVSTRow>
              ))
            )}
            <ContentContainer>
              {!this.state.searchResults === false && (
                <ResultsContainer>
                  {this.state.searchResults.map((result, index) => {
                    return (
                      <div key={index}>
                        <Result
                          isFocused={this.state.currentlySelectedResultIndex === index}
                          onClick={e => this.handleResultInteraction(result)}
                          onMouseEnter={() => {
                            this.selectResult(index);
                          }}
                        >
                          <ResultDetailsContainer>
                            <ResultName>{result.code}</ResultName>
                            <ResultDescription>{this.getResultDescription(result)}</ResultDescription>
                          </ResultDetailsContainer>
                          <ResultValue
                            hideTickerPrice={this.props.hideTickerPrice}
                            currency={result.currency}
                            value={this.getResultValue(result)}
                            abbreviate={false}
                            roundDown={true}
                          />
                        </Result>
                      </div>
                    );
                  })}
                  {this.state.searchResults.length === 0 && (
                    <EmptyResult>
                      {this.allowUnusedTickers() ? i18n.t("nothingFound") : i18n.t("assetNotFound")}
                    </EmptyResult>
                  )}
                </ResultsContainer>
              )}
            </ContentContainer>
          </Container>
        </PickerDialog>
      </DialogOverlay>
    );
  }
}

const mapStateToProps = (state, props) => ({
  currentPortfolio: currentPortfolioSelector(state),
  privateCustodians: getPrivateCustodians(state),
  planningRules: planningRulesSelector(state)
});

const mapDispatchToProps = {
  searchTickersForText: searchTickersForText,
  getTickersForIds: getTickersForIds
};

const TickersPickerDialogWrapper = props => {
  const store = useStore();
  return <TickersPickerDialog {...props} store={store} />;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TickersPickerDialogWrapper);
